import React, { useState, useEffect } from 'react';
import { Button, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import { getUserData, changePassword, getEmailPreferences, updateEmailPreferences, logoutAll } from '../../api/Api';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { deleteUser } from "../../api/Api";
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import { useColors } from '../../theme/ColorModeContext';

interface PropsSettings {
    token: string;
    setToken: React.Dispatch<React.SetStateAction<any>>;
}

const Settings = (props: PropsSettings) => {
    const colors = useColors();
    const token = props.token;
    const setToken = props.setToken;

    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [openDelete, setOpenDelete] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [savingPrefs, setSavingPrefs] = useState(false);
    const [prefsMessage, setPrefsMessage] = useState("");
    const [signingOutAll, setSigningOutAll] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getUserData(token);
            setUsername(data.username);
            setEmail(data.email);

            const prefs = await getEmailPreferences(token);
            if (prefs) setMarketingConsent(prefs.marketing_consent);
        }
        fetchData();
    }, []);

    const handleMarketingToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.checked;
        // Optimistic, then reconciled against what the API actually stored, so a
        // failed opt-out never leaves the UI claiming the user is unsubscribed.
        setMarketingConsent(next);
        setSavingPrefs(true);
        const prefs = await updateEmailPreferences(token, next, "settings");
        setSavingPrefs(false);

        if (!prefs) {
            setMarketingConsent(!next);
            setPrefsMessage("Could not save your preference. Please try again.");
            return;
        }
        setMarketingConsent(prefs.marketing_consent);
        setPrefsMessage(prefs.marketing_consent
            ? "Subscribed to product updates."
            : "Unsubscribed. You will still get account and security emails.");
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const old_password = `${data.get("oldPassword")}`;
        const new_password = `${data.get("newPassword")}`;
        const confirm_new_password = `${data.get("confirmNewPassword")}`;
        const response = await changePassword(token, { old_password, new_password, confirm_new_password });
        setMessage(response.msg);
        // Changing the password revokes every token issued for the account,
        // this one included. The server hands back a fresh one so the person
        // who just changed it stays signed in; without storing it, the next
        // poll would sign them out too.
        if (response.access_token) {
            setToken(response.access_token);
        }
    };

    const handleSignOutAll = async () => {
        setSigningOutAll(true);
        await logoutAll(token);
        // The current session is revoked along with the rest, so there is
        // nothing left to stay signed in with.
        setToken("");
    };

    const handleDelete = async (e: any) => {
        e.preventDefault();
        await deleteUser(token);
        setToken("");
    }

    return (
        <PageContainer>
            <Typography variant="h3" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '24px' }}>
                Settings
            </Typography>

            <GlowCard glowOnHover={false} sx={{ padding: '24px', marginBottom: '32px' }}>
                {username && email ? (
                    <Box>
                        <Typography variant="body1" sx={{ color: colors.text.secondary, marginBottom: '4px' }}>
                            Username: <Box component="span" sx={{ color: colors.text.primary, fontWeight: 500 }}>{username}</Box>
                        </Typography>
                        <Typography variant="body1" sx={{ color: colors.text.secondary }}>
                            Email: <Box component="span" sx={{ color: colors.text.primary, fontWeight: 500 }}>{email}</Box>
                        </Typography>
                    </Box>
                ) : (
                    <CircularProgress size={40} />
                )}
            </GlowCard>

            <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '16px' }}>
                Email Preferences
            </Typography>
            <GlowCard glowOnHover={false} sx={{ padding: '24px', marginBottom: '40px' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px' }}>
                    <Box>
                        <Typography variant="body1" sx={{ color: colors.text.primary, fontWeight: 600 }}>
                            Product updates
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.secondary, marginTop: '4px', maxWidth: '520px' }}>
                            New challenges, new tracks and platform news. A couple of times a month at most.
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.muted, marginTop: '10px', maxWidth: '520px' }}>
                            Account and security emails (confirmation, password resets, changes to these
                            terms) are part of your account and are sent regardless of this setting.
                        </Typography>
                    </Box>
                    <Switch
                        checked={marketingConsent}
                        onChange={handleMarketingToggle}
                        disabled={savingPrefs}
                        inputProps={{ 'aria-label': 'Product update emails' }}
                    />
                </Box>
                {prefsMessage && (
                    <Typography variant="body2" sx={{
                        color: colors.accent.cyan,
                        marginTop: '16px',
                        padding: '8px 12px',
                        backgroundColor: colors.accent.cyanGlow,
                        borderRadius: '8px',
                    }}>
                        {prefsMessage}
                    </Typography>
                )}
            </GlowCard>

            <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '16px' }}>
                Change Password
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '50%', marginBottom: '40px' }}>
                <TextField margin="normal" required fullWidth id="oldPassword" label="Old Password" name="oldPassword" type="password" autoFocus />
                <TextField margin="normal" required fullWidth id="newPassword" label="New Password" name="newPassword" type="password" />
                <TextField margin="normal" required fullWidth name="confirmNewPassword" id="confirmNewPassword" label="Confirm New Password" type="password" />
                <Button type="submit" fullWidth variant="outlined" sx={{
                    mt: 3, mb: 2, padding: '12px',
                    color: colors.text.primary,
                    borderColor: colors.border.strong,
                    '&:hover': {
                        backgroundColor: colors.bg.surface,
                        borderColor: colors.text.secondary,
                    },
                }}>
                    Update
                </Button>
                {message && (
                    <Typography variant="body2" sx={{
                        color: colors.accent.cyan,
                        marginTop: '8px',
                        padding: '8px 12px',
                        backgroundColor: colors.accent.cyanGlow,
                        borderRadius: '8px',
                    }}>
                        {message}
                    </Typography>
                )}
            </Box>

            <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '16px' }}>
                Active Sessions
            </Typography>
            <Box sx={{ width: '50%', marginBottom: '40px' }}>
                <Typography variant="body2" sx={{ color: colors.text.secondary, marginBottom: '16px' }}>
                    Signs out every browser and device signed in to this account, including
                    this one. Use it if you think someone else has your password — then
                    change it.
                </Typography>
                <Button
                    fullWidth
                    onClick={handleSignOutAll}
                    disabled={signingOutAll}
                    variant="outlined"
                    sx={{
                        padding: '12px',
                        color: colors.text.primary,
                        borderColor: colors.border.strong,
                        '&:hover': {
                            backgroundColor: colors.bg.surface,
                            borderColor: colors.text.secondary,
                        },
                    }}
                >
                    {signingOutAll ? 'Signing out…' : 'Sign out of all devices'}
                </Button>
            </Box>

            <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '16px', color: colors.accent.red }}>
                Danger Zone
            </Typography>
            <Box sx={{ width: '50%' }}>
                <Button
                    fullWidth
                    onClick={() => setOpenDelete(true)}
                    variant="outlined"
                    color="error"
                    sx={{ padding: '12px' }}
                >
                    Delete User
                </Button>
            </Box>

            <Modal
              open={openDelete}
              onClose={() => setOpenDelete(false)}
            >
              <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '500px',
                  maxWidth: '90vw',
                  backgroundColor: colors.bg.secondary,
                  border: `1px solid ${colors.border.default}`,
                  borderRadius: '8px',
                  overflow: 'hidden',
              }}>
                <Typography variant="h5" sx={{
                    padding: '20px 24px',
                    fontFamily: '"Orbitron", sans-serif',
                    borderBottom: `1px solid ${colors.border.subtle}`,
                    backgroundColor: colors.bg.tertiary,
                }}>
                    User Deletion
                </Typography>
                <Box sx={{ padding: '24px' }}>
                    <Typography variant="body1" sx={{ color: colors.text.secondary }}>
                        The following data will be deleted:
                    </Typography>
                    <ul style={{ color: colors.text.secondary }}>
                        <li>All user data</li>
                        <li>All solved challenges</li>
                        <li>All points</li>
                    </ul>
                    <Typography variant="h6" sx={{ fontFamily: '"Orbitron", sans-serif', marginTop: '16px', marginBottom: '20px' }}>
                        Are you sure you want to delete your account?
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Button fullWidth variant="contained" onClick={() => setOpenDelete(false)}>
                                No, abort!
                            </Button>
                        </Grid>
                        <Grid item xs={6}>
                            <Button fullWidth color="error" variant="contained" onClick={handleDelete}
                                sx={{
                                    background: colors.accent.red,
                                    '&:hover': {
                                        background: '#d50000',
                                        boxShadow: `0 0 20px ${colors.accent.redGlow}`,
                                    },
                                }}
                            >
                                Yes, Delete!
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
              </Box>
            </Modal>
        </PageContainer>
    );
}

export default Settings;
